/**
 * HIPAA breach notification templates (Module 4).
 *
 * Generates Word (.docx) documents for the three notification types required
 * by 45 CFR §164.404 and related sections:
 *
 *   1. Individual notice          — 45 CFR §164.404(c)
 *   2. Media notice               — 45 CFR §164.406  (≥500 individuals in a state)
 *   3. Substitute notice          — 45 CFR §164.404(d)(2)  (when individual
 *                                    contact info is insufficient or out-of-date)
 *
 * Each template honors the CONTENT REQUIREMENTS of §164.404(c):
 *
 *   (A) Brief description of what happened, including the date of the breach
 *       and the date of the discovery of the breach, if known
 *   (B) Description of the types of unsecured protected health information
 *       that were involved
 *   (C) Any steps individuals should take to protect themselves from potential
 *       harm resulting from the breach
 *   (D) Brief description of what the covered entity involved is doing to
 *       investigate, mitigate harm to individuals, and protect against
 *       further breaches
 *   (E) Contact procedures for individuals to ask questions or learn
 *       additional information (toll-free number, email, website, postal
 *       address)
 *
 * Fields marked [PLACEHOLDER: ...] in the generated doc are for the user
 * to fill in from the merge-field form BEFORE download, or to edit in Word
 * if left blank.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType
} from 'docx';
import { saveAs } from 'file-saver';
import {
  protectionsContent,
  monitoringOfferContent,
  stateContentFor,
  MONITORING_MERGE_FIELDS,
} from './noticeContent';

// ---------------------------------------------------------------------------
// Catalog — exposed to the UI for the template picker
// ---------------------------------------------------------------------------
export const TEMPLATE_CATALOG = [
  {
    id: 'individual',
    title: 'Individual notice letter',
    subtitle: '45 CFR §164.404(c)',
    description: 'Letter to each affected individual, satisfying the five required content elements (A)-(E).',
    icon: 'user',
    builder: 'buildIndividualNotice',
    mergeFields: [
      { key: 'orgName', label: 'Your organization name', placeholder: 'e.g. Acme Medicare Advantage Plan', required: true },
      { key: 'orgContactPhone', label: 'Toll-free inquiry phone', placeholder: 'e.g. 1-800-555-0199', required: true },
      { key: 'orgContactEmail', label: 'Inquiry email', placeholder: 'e.g. privacy@acme.com', required: true },
      { key: 'orgContactAddress', label: 'Postal address', placeholder: 'e.g. 123 Main St, Boston, MA 02108', required: true },
      { key: 'orgWebsite', label: 'Website for additional info', placeholder: 'e.g. https://acme.com/privacy-incident', required: false },
      { key: 'officerName', label: 'Privacy officer name', placeholder: 'e.g. Jane Smith', required: true },
      { key: 'officerTitle', label: 'Privacy officer title', placeholder: 'e.g. Chief Privacy Officer', required: true },
      { key: 'recipientName', label: 'Recipient name (optional — can be filled per-letter)', placeholder: 'leave blank to use [RECIPIENT NAME] placeholder', required: false },
      { key: 'stepsToProtect', label: 'Incident-specific steps (optional — the standard protections guidance, credit bureaus, fraud alerts, freezes, FTC, is included automatically)', placeholder: 'e.g. If you receive a call claiming to be from us demanding payment, hang up and call the number on your member ID card', required: false, multiline: true },
      ...MONITORING_MERGE_FIELDS
    ]
  },
  {
    id: 'media',
    title: 'Media notice',
    subtitle: '45 CFR §164.406 · ≥500 residents in a state/jurisdiction',
    description: 'Press-release format for distribution to prominent media outlets in any state where ≥500 residents are affected.',
    icon: 'megaphone',
    builder: 'buildMediaNotice',
    mergeFields: [
      { key: 'orgName', label: 'Your organization name', placeholder: 'e.g. Acme Medicare Advantage Plan', required: true },
      { key: 'mediaOutlets', label: 'Target state(s) / media markets', placeholder: 'e.g. California — LA Times, SF Chronicle', required: true },
      { key: 'orgContactPhone', label: 'Media inquiry phone', placeholder: 'e.g. 1-800-555-0199', required: true },
      { key: 'orgContactEmail', label: 'Media inquiry email', placeholder: 'e.g. media@acme.com', required: true },
      { key: 'officerName', label: 'Privacy officer name', placeholder: 'e.g. Jane Smith', required: true },
      { key: 'officerTitle', label: 'Privacy officer title', placeholder: 'e.g. Chief Privacy Officer', required: true },
      { key: 'stepsToProtect', label: 'Steps affected individuals should take', placeholder: 'e.g. Call the toll-free number or visit the dedicated webpage', required: true, multiline: true }
    ]
  },
  {
    id: 'substitute',
    title: 'Substitute notice',
    subtitle: '45 CFR §164.404(d)(2)',
    description: 'Alternative notice used when individual contact info is insufficient or out-of-date. If ≥10 individuals, requires website posting for 90 days OR notice in major print/broadcast media.',
    icon: 'globe',
    builder: 'buildSubstituteNotice',
    mergeFields: [
      { key: 'orgName', label: 'Your organization name', placeholder: 'e.g. Acme Medicare Advantage Plan', required: true },
      { key: 'orgContactPhone', label: 'Toll-free inquiry phone', placeholder: 'e.g. 1-800-555-0199', required: true },
      { key: 'orgWebsite', label: 'Website URL where this will be posted', placeholder: 'e.g. https://acme.com/privacy-incident', required: true },
      { key: 'distributionMethod', label: 'Distribution method', placeholder: 'e.g. Website posting for 90 days; also ran in LA Times', required: true, multiline: true },
      { key: 'officerName', label: 'Privacy officer name', placeholder: 'e.g. Jane Smith', required: true },
      { key: 'officerTitle', label: 'Privacy officer title', placeholder: 'e.g. Chief Privacy Officer', required: true },
      ...MONITORING_MERGE_FIELDS
    ]
  }
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const PLACEHOLDER = (field) => `[PLACEHOLDER: ${field}]`;
const mergeOrPlaceholder = (value, field) => (value && String(value).trim()) || PLACEHOLDER(field);

// Timezone-safe date formatter. A bare ISO date like "2026-04-08" parsed via
// `new Date()` becomes UTC midnight, which when formatted via toLocaleDateString
// in a US timezone produces the previous day. For a legal notice that's a real
// bug, so we parse the date parts directly and format manually.
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const fmtDate = (iso) => {
  if (!iso) return PLACEHOLDER('date');
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  const day = parseInt(m[3], 10);
  if (!year || !month || !day) return iso;
  return `${MONTHS[month - 1]} ${day}, ${year}`;
};

// ---------------------------------------------------------------------------
// Shared paragraph helpers
// ---------------------------------------------------------------------------
const p = (text, options = {}) => new Paragraph({
  children: [new TextRun({ text, ...options.textRun })],
  spacing: options.spacing || { after: 120 },
  alignment: options.alignment,
  heading: options.heading
});

const pb = (text) => new Paragraph({
  children: [new TextRun({ text, bold: true })],
  spacing: { after: 120 }
});

const p2 = (...runs) => new Paragraph({
  children: runs.map(r => typeof r === 'string' ? new TextRun({ text: r }) : new TextRun(r)),
  spacing: { after: 120 }
});

// The "date of discovery of the breach" required by 164.404(c)(1)(A) is the
// day the breach was known or should have been known (breach_discovered_date,
// the clock anchor), NOT first awareness of an incident (discovery_date).
function discoveryDateFor(incident) {
  return incident.breach_discovered_date || incident.breach_determined_date || incident.discovery_date;
}

// Render engine content sections ({heading, paragraphs, bullets}) into docx
// paragraphs, matching the letter's typography.
function renderContentSections(sections, { headingSize = 22 } = {}) {
  const out = [];
  for (const s of sections || []) {
    if (!s) continue;
    if (s.heading) {
      out.push(new Paragraph({
        children: [new TextRun({ text: s.heading, bold: true, size: headingSize })],
        spacing: { before: 140, after: 100 },
      }));
    }
    for (const text of s.paragraphs || []) out.push(p(text));
    for (const b of s.bullets || []) out.push(p(`  • ${b}`));
  }
  return out;
}

// Per-state statutory additions + the loud review box for states without a
// verified content module. A selected state can never fall through silently.
function renderStateAddenda(stateCodes) {
  const { verified, reviewRequired } = stateContentFor(stateCodes);
  const out = [];
  for (const mod of verified) {
    if (!mod.additions || !mod.additions.length) continue;
    out.push(new Paragraph({
      children: [new TextRun({ text: `Additional information for residents of ${mod.state_name}`, bold: true, size: 22 })],
      spacing: { before: 140, after: 100 },
    }));
    for (const text of mod.additions) out.push(p(text));
    out.push(new Paragraph({
      children: [new TextRun({ text: `Provided pursuant to ${mod.citation}.`, italics: true, size: 16, color: '666666' })],
      spacing: { after: 120 },
    }));
  }
  if (reviewRequired.length) {
    out.push(new Paragraph({
      children: [new TextRun({
        text: `[STATE CONTENT REVIEW REQUIRED before mailing: ${reviewRequired.join(', ')} — no verified content module exists for ${reviewRequired.length === 1 ? 'this state' : 'these states'}. Check each state's breach notification statute (see the Law Library) for content this letter must add for those residents, then remove this box.]`,
        bold: true, color: 'C0392B',
      })],
      spacing: { before: 160, after: 160 },
    }));
  }
  return out;
}

// Build the description of what happened from the incident data
function describeIncident(incident) {
  const parts = [];
  if (incident.facts) parts.push(incident.facts);
  else if (incident.summary) parts.push(incident.summary);
  else parts.push(PLACEHOLDER('description of what happened'));
  return parts.join(' ');
}

function describePhi(incident) {
  return mergeOrPlaceholder(incident.type_of_phi, 'types of unsecured PHI involved');
}

function describeMitigation(incident) {
  const parts = [];
  if (incident.actions_taken) parts.push(incident.actions_taken);
  if (incident.safeguards_in_place) parts.push(`Safeguards already in place at the time of the incident: ${incident.safeguards_in_place}`);
  if (parts.length === 0) parts.push(PLACEHOLDER('what your organization is doing to investigate, mitigate, and prevent further breaches'));
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// 1. Individual Notice — 45 CFR §164.404(c)
// ---------------------------------------------------------------------------
export function buildIndividualNotice(incident, merge = {}) {
  const orgName = mergeOrPlaceholder(merge.orgName, 'ORGANIZATION NAME');
  const todayLong = fmtDate(new Date().toISOString().slice(0, 10));
  const stateCodes = Array.isArray(merge.stateCodes) ? merge.stateCodes : [];
  // California prescribes the notice title and headings (Civ. Code §1798.82(d));
  // the CA-canonical set satisfies the federal elements too, so it is the
  // default and the title switches when CA residents are addressed.
  const caFormat = stateCodes.map((s) => String(s).toUpperCase()).includes('CA');

  const children = [
    // Letterhead
    new Paragraph({
      children: [new TextRun({ text: orgName, bold: true, size: 28 })],
      alignment: AlignmentType.LEFT,
      spacing: { after: 80 }
    }),
    p(mergeOrPlaceholder(merge.orgContactAddress, 'postal address'), { alignment: AlignmentType.LEFT }),
    new Paragraph({ text: '', spacing: { after: 240 } }),

    // Date
    p(todayLong, { alignment: AlignmentType.LEFT }),
    new Paragraph({ text: '', spacing: { after: 120 } }),

    // Recipient
    p(mergeOrPlaceholder(merge.recipientName, 'RECIPIENT NAME')),
    p('[PLACEHOLDER: Recipient street address]'),
    p('[PLACEHOLDER: Recipient city, state ZIP]'),
    new Paragraph({ text: '', spacing: { after: 200 } }),

    // Subject — California requires the title "Notice of Data Breach"
    new Paragraph({
      children: [new TextRun({ text: caFormat ? 'NOTICE OF DATA BREACH' : 'RE: Notice of Unauthorized Disclosure of Your Protected Health Information', bold: true })],
      spacing: { after: 240 }
    }),

    // Salutation
    p2({ text: 'Dear ' }, { text: mergeOrPlaceholder(merge.recipientName, 'Recipient') }, { text: ',' }),
    new Paragraph({ text: '', spacing: { after: 120 } }),

    // Opening
    p2(
      { text: 'We are writing to inform you of a recent incident that may have involved some of your protected health information ("PHI"). ' },
      { text: orgName, bold: true },
      { text: ' takes the privacy and security of your information very seriously, and we want to provide you with the details of this incident and the steps we are taking in response.' }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // (A) WHAT HAPPENED — 164.404(c)(1)(A)
    new Paragraph({
      children: [new TextRun({ text: 'What Happened', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2(
      { text: `On ${fmtDate(discoveryDateFor(incident))}, we discovered ` },
      { text: incident.breach_date ? `an incident that is believed to have occurred on or about ${fmtDate(incident.breach_date)}. ` : 'an incident. ' },
      { text: describeIncident(incident) }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // (B) TYPES OF PHI — 164.404(c)(1)(B)
    new Paragraph({
      children: [new TextRun({ text: 'What Information Was Involved', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2(
      { text: 'The types of unsecured protected health information that may have been involved include: ' },
      { text: describePhi(incident) },
      { text: '.' }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // (D) WHAT WE ARE DOING — 164.404(c)(1)(D). Placed before the protections
    // section to follow the California-canonical order, which reads naturally
    // everywhere and is mandatory for CA residents.
    new Paragraph({
      children: [new TextRun({ text: 'What We Are Doing', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(describeMitigation(incident)),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // (C) STEPS INDIVIDUALS SHOULD TAKE — 164.404(c)(1)(C): incident-specific
    // guidance from the officer (if any), then the standard protections block
    // (credit reports, bureaus, fraud alerts, freezes, FTC, Medicare), then
    // the monitoring offer when the plan is providing services.
    new Paragraph({
      children: [new TextRun({ text: 'What You Can Do', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    ...(merge.stepsToProtect && String(merge.stepsToProtect).trim() ? [p(String(merge.stepsToProtect).trim())] : []),
    ...renderContentSections(protectionsContent({ medicare: true })),
    ...renderContentSections([monitoringOfferContent(merge)].filter(Boolean)),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // (E) CONTACT PROCEDURES — 164.404(c)(1)(E)
    new Paragraph({
      children: [new TextRun({ text: 'For More Information', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2(
      { text: 'If you have any questions or would like additional information, please contact us using any of the following methods:' }
    ),
    p2({ text: '  • Toll-free phone: ' }, { text: mergeOrPlaceholder(merge.orgContactPhone, 'toll-free phone') }),
    p2({ text: '  • Email: ' }, { text: mergeOrPlaceholder(merge.orgContactEmail, 'inquiry email') }),
    p2({ text: '  • Postal mail: ' }, { text: mergeOrPlaceholder(merge.orgContactAddress, 'postal address') }),
    ...(merge.orgWebsite ? [p2({ text: '  • Website: ' }, { text: merge.orgWebsite })] : []),
    new Paragraph({ text: '', spacing: { after: 240 } }),

    // State-mandated additions + loud review box for unverified states
    ...renderStateAddenda(stateCodes),

    // Closing
    p('We sincerely apologize for any inconvenience or concern this incident may cause you. Protecting your privacy is one of our highest priorities, and we are committed to preventing incidents like this in the future.'),
    new Paragraph({ text: '', spacing: { after: 240 } }),

    p('Sincerely,'),
    new Paragraph({ text: '', spacing: { after: 360 } }),
    pb(mergeOrPlaceholder(merge.officerName, 'Privacy Officer Name')),
    p(mergeOrPlaceholder(merge.officerTitle, 'Privacy Officer Title')),
    p(orgName),

    // Footer: regulatory reference
    new Paragraph({
      children: [
        new TextRun({
          text: '\n\nThis notice is provided in accordance with the breach notification requirements of the Health Insurance Portability and Accountability Act (HIPAA), 45 CFR §164.404.',
          italics: true,
          size: 18,
          color: '666666'
        })
      ],
      spacing: { before: 480 }
    })
  ];

  const doc = new Document({ sections: [{ children }] });
  return Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// 2. Media Notice — 45 CFR §164.406
// ---------------------------------------------------------------------------
export function buildMediaNotice(incident, merge = {}) {
  const orgName = mergeOrPlaceholder(merge.orgName, 'ORGANIZATION NAME');
  const todayLong = fmtDate(new Date().toISOString().slice(0, 10));
  const numberAffected = incident.number_affected ? String(incident.number_affected) : PLACEHOLDER('number of individuals affected');

  const children = [
    // Header — press release style
    new Paragraph({
      children: [new TextRun({ text: 'FOR IMMEDIATE RELEASE', bold: true, size: 24 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({ text: todayLong, size: 22 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 }
    }),

    // Title
    new Paragraph({
      children: [new TextRun({ text: `${orgName} Notice of Protected Health Information Breach`, bold: true, size: 30 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 240 }
    }),

    // Subtitle — target markets
    new Paragraph({
      children: [
        new TextRun({ text: 'Target markets: ', italics: true, size: 20 }),
        new TextRun({ text: mergeOrPlaceholder(merge.mediaOutlets, 'target markets'), italics: true, size: 20 })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 }
    }),

    // Opening
    p2(
      { text: orgName, bold: true },
      { text: ` is providing notice of a recent incident that may have involved the unsecured protected health information of approximately ${numberAffected} individuals. This notice is being provided in accordance with 45 CFR §164.406 because the number of affected individuals in this jurisdiction meets or exceeds 500.` }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // What happened
    new Paragraph({
      children: [new TextRun({ text: 'Incident description', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2(
      { text: `The incident was discovered on ${fmtDate(discoveryDateFor(incident))}. ` },
      { text: describeIncident(incident) }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Information involved
    new Paragraph({
      children: [new TextRun({ text: 'Information involved', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(`The types of protected health information potentially involved include: ${describePhi(incident)}.`),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Response
    new Paragraph({
      children: [new TextRun({ text: 'Our response', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(describeMitigation(incident)),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Steps for individuals
    new Paragraph({
      children: [new TextRun({ text: 'What affected individuals should do', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(mergeOrPlaceholder(merge.stepsToProtect, 'describe steps for affected individuals to protect themselves')),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Contact
    new Paragraph({
      children: [new TextRun({ text: 'Media contact', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2({ text: 'Phone: ' }, { text: mergeOrPlaceholder(merge.orgContactPhone, 'media contact phone') }),
    p2({ text: 'Email: ' }, { text: mergeOrPlaceholder(merge.orgContactEmail, 'media contact email') }),
    new Paragraph({ text: '', spacing: { after: 240 } }),

    // Signature block
    p('For additional information:'),
    pb(mergeOrPlaceholder(merge.officerName, 'Privacy Officer Name')),
    p(mergeOrPlaceholder(merge.officerTitle, 'Privacy Officer Title')),
    p(orgName),

    new Paragraph({
      children: [new TextRun({ text: '\n# # #', bold: true, size: 22 })],
      alignment: AlignmentType.CENTER,
      spacing: { before: 360 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: '\nProvided under the breach notification requirements of HIPAA, 45 CFR §164.406.',
          italics: true,
          size: 18,
          color: '666666'
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 360 }
    })
  ];

  const doc = new Document({ sections: [{ children }] });
  return Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// 3. Substitute Notice — 45 CFR §164.404(d)(2)
// ---------------------------------------------------------------------------
export function buildSubstituteNotice(incident, merge = {}) {
  const orgName = mergeOrPlaceholder(merge.orgName, 'ORGANIZATION NAME');
  const todayLong = fmtDate(new Date().toISOString().slice(0, 10));

  const children = [
    new Paragraph({
      children: [new TextRun({ text: 'NOTICE OF PROTECTED HEALTH INFORMATION INCIDENT', bold: true, size: 30 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `${orgName}`, bold: true, size: 26 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `Posted: ${todayLong}`, italics: true, size: 20 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 }
    }),

    // Regulatory context
    p2(
      { text: 'This substitute notice is provided under ' },
      { text: '45 CFR §164.404(d)(2)', italics: true },
      { text: ' because ' },
      { text: orgName },
      { text: ' was unable to reach some affected individuals using current contact information on file. This notice appears in the following locations: ' },
      { text: mergeOrPlaceholder(merge.distributionMethod, 'describe distribution method — e.g. posted on website for 90 days; ran in prominent newspaper'), italics: true }
    ),
    new Paragraph({ text: '', spacing: { after: 240 } }),

    // What happened
    new Paragraph({
      children: [new TextRun({ text: 'What happened', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p2(
      { text: `On ${fmtDate(discoveryDateFor(incident))}, we discovered an incident ` },
      { text: incident.breach_date ? `believed to have occurred on or about ${fmtDate(incident.breach_date)}. ` : 'that may have affected some of our members. ' },
      { text: describeIncident(incident) }
    ),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Information involved
    new Paragraph({
      children: [new TextRun({ text: 'Information involved', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(`The types of protected health information potentially involved include: ${describePhi(incident)}.`),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Response
    new Paragraph({
      children: [new TextRun({ text: 'What we are doing', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    p(describeMitigation(incident)),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Steps for affected individuals — standard protections + monitoring offer
    new Paragraph({
      children: [new TextRun({ text: 'Steps you can take to protect yourself', bold: true, size: 24 })],
      spacing: { before: 160, after: 120 }
    }),
    ...renderContentSections(protectionsContent({ medicare: true })),
    ...renderContentSections([monitoringOfferContent(merge)].filter(Boolean)),
    new Paragraph({ text: '', spacing: { after: 160 } }),

    // Contact instructions — highly prominent for substitute notice
    new Paragraph({
      children: [new TextRun({ text: 'If you believe you may be affected', bold: true, size: 26, color: 'C0392B' })],
      spacing: { before: 240, after: 120 }
    }),
    p('Please contact us immediately using the toll-free number below so we can determine whether your information was involved and provide specific guidance:'),
    new Paragraph({ text: '', spacing: { after: 120 } }),
    new Paragraph({
      children: [new TextRun({ text: `☎  ${mergeOrPlaceholder(merge.orgContactPhone, 'toll-free phone')}`, bold: true, size: 28 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 }
    }),
    new Paragraph({
      children: [new TextRun({ text: `🌐  ${mergeOrPlaceholder(merge.orgWebsite, 'website URL')}`, bold: true, size: 24 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 }
    }),

    // Signature
    p(`Issued by ${orgName}`),
    pb(mergeOrPlaceholder(merge.officerName, 'Privacy Officer Name')),
    p(mergeOrPlaceholder(merge.officerTitle, 'Privacy Officer Title')),

    new Paragraph({
      children: [
        new TextRun({
          text: '\nThis notice is provided under the breach notification requirements of HIPAA, 45 CFR §164.404(d)(2). For more information about HIPAA breach notification, visit hhs.gov/hipaa.',
          italics: true,
          size: 18,
          color: '666666'
        })
      ],
      spacing: { before: 360 }
    })
  ];

  const doc = new Document({ sections: [{ children }] });
  return Packer.toBlob(doc);
}

// ---------------------------------------------------------------------------
// Unified downloader — used by the UI
// ---------------------------------------------------------------------------
export async function downloadTemplate(templateId, incident, merge = {}) {
  const builders = {
    individual: buildIndividualNotice,
    media: buildMediaNotice,
    substitute: buildSubstituteNotice
  };
  const builder = builders[templateId];
  if (!builder) throw new Error(`Unknown template: ${templateId}`);
  const blob = await builder(incident, merge);
  const shortId = incident?.id ? String(incident.id).slice(0, 8) : 'incident';
  const today = new Date().toISOString().slice(0, 10);
  const name = `${templateId}-notice-${shortId}-${today}.docx`;
  saveAs(blob, name);
  return name;
}
